// MobileAddClient — form veloce per aggiungere lead in mobilità
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Loader2, Phone, User, Mail, MapPin, Calendar as CalIcon, FileText, Check } from "lucide-react";
import MobileShell from "@/components/mobile/MobileShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { buzz } from "@/lib/mobile";

const STATI_QUICK = ["NUOVO", "INTERESSATO", "PROPORRE PP", "RICHIAMARE", "ATTESA DOCUMENTI"];

export default function MobileAddClient() {
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    nome: "", cognome: "", telefono: "", mail: "",
    citta: "", cf: "", fonte: "", stato: "NUOVO",
    data: "", note: "",
  });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e?.preventDefault();
    if (!f.nome.trim() && !f.cognome.trim() && !f.telefono.trim()) {
      return toast.error("Inserisci almeno nome o telefono");
    }
    setSaving(true);
    try {
      const r = await api.post("/clients", f);
      toast.success("Cliente creato");
      buzz(20);
      nav(`/m/clienti/${r.data.id}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    } finally { setSaving(false); }
  };

  return (
    <MobileShell title="Nuovo cliente" subtitle="Aggiungi al volo" back
      action={
        <button onClick={submit} disabled={saving} data-testid="madd-save"
                className="text-[#B7955B] font-semibold text-sm px-2 py-1 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
        </button>
      }
    >
      <form onSubmit={submit} className="space-y-3" data-testid="madd-form">
        {/* Anagrafica */}
        <Card title="Anagrafica">
          <Input label="Cognome" icon={User} value={f.cognome} onChange={(v) => upd("cognome", v)} testid="madd-cognome" />
          <Input label="Nome" icon={User} value={f.nome} onChange={(v) => upd("nome", v)} testid="madd-nome" />
          <Input label="Telefono" icon={Phone} type="tel" inputMode="tel" autoComplete="tel"
                 value={f.telefono} onChange={(v) => upd("telefono", v)} testid="madd-tel" />
          <Input label="Email" icon={Mail} type="email" inputMode="email" autoComplete="email"
                 value={f.mail} onChange={(v) => upd("mail", v)} testid="madd-mail" />
          <Input label="Codice Fiscale" value={f.cf} onChange={(v) => upd("cf", v.toUpperCase())} testid="madd-cf"
                 placeholder="RSSMRA80A01H501U" />
          <Input label="Città" icon={MapPin} value={f.citta} onChange={(v) => upd("citta", v)} testid="madd-citta" />
        </Card>

        {/* Stato + recall */}
        <Card title="Stato & Recall">
          <Field label="Stato">
            <div className="flex flex-wrap gap-1.5">
              {STATI_QUICK.map((s) => (
                <button key={s} type="button" onClick={() => upd("stato", s)}
                        data-testid={`madd-stato-${s}`}
                        className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium ${f.stato === s ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-700"}`}>
                  {s}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Data recall">
            <input type="date" value={f.data} onChange={(e) => upd("data", e.target.value)}
                   data-testid="madd-data"
                   className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-[10px] bg-white tabular-nums" />
          </Field>
          <Input label="Fonte (segnalatore o canale)" value={f.fonte} onChange={(v) => upd("fonte", v)} testid="madd-fonte"
                 placeholder="es: Segnalazione Marco / OPEN FINANCE" />
        </Card>

        {/* Note */}
        <Card title="Note">
          <textarea value={f.note} onChange={(e) => upd("note", e.target.value)}
                    rows={4} placeholder="Note libere…"
                    data-testid="madd-note"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-[10px] resize-y bg-white" />
        </Card>

        <button type="submit" disabled={saving}
                data-testid="madd-submit"
                className="w-full bg-[#0A192F] text-white py-3 rounded-[10px] font-medium flex items-center justify-center gap-2 disabled:opacity-50 btn-press">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {saving ? "Salvataggio..." : "Crea cliente"}
        </button>
      </form>
    </MobileShell>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white overflow-hidden" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }}>
      <div className="px-3 py-2 bg-[#F4F6FA] border-b border-slate-100">
        <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">{title}</span>
      </div>
      <div className="p-3 space-y-2.5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function Input({ label, value, onChange, type = "text", inputMode, autoComplete, icon: Icon, placeholder, testid }) {
  return (
    <Field label={label}>
      <div className={`flex items-center bg-white" style={{ border: "1px solid #E8ECF2", borderRadius: 12, boxShadow: "0 1px 3px rgba(10,25,47,0.04)" }} className=" focus-within:border-[#0A192F] ${Icon ? "pl-3" : ""}`}>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <input
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none"
        />
      </div>
    </Field>
  );
}
